import { getApiUrl } from "../../../utils/apiConfig";
import { sentryFetch } from "../../../utils/sentryFetchWrapper";
import { getAuthToken, setAuthToken } from "../../../utils/authToken";
import { getKetchConsent } from "../../../utils/ketchConsent";
import {
  ExistingPatientInput,
  NewPatientInput,
  prepareExistingPatientSubmissionPayload,
  prepareNewPatientSubmissionPayload,
} from "./preparePayload";
import {
  ApiError,
  apiResponseSchema,
  NetworkError,
  ValidationError,
  NewPatientSubmissionPayload,
  ExistingPatientSubmissionPayload,
} from "./types";

async function makeApiCall(apiUrl: string, payload: NewPatientSubmissionPayload | ExistingPatientSubmissionPayload) {
  try {
    const token = getAuthToken();
    const response = await sentryFetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
      credentials: "include",
      ignoredStatusCodes: [401],
    });

    if (!response.ok) {
      // Handle HTTP error responses
      const errorData = await response.json().catch(() => null);
      throw new ApiError(
        `API request failed with status ${response.status}`,
        response.status,
        errorData,
        payload,
      );
    }

    const data = await response.json();
    const authHeader = response.headers.get("Authorization");
    if (authHeader) setAuthToken(authHeader);
    return apiResponseSchema.parse(data);
  } catch (error) {
    // Rethrow one of our custom errors if it matches
    if (
      error instanceof ApiError ||
      error instanceof NetworkError ||
      error instanceof ValidationError
    ) {
      throw error;
    }

    // Rethrow network errors with proper typing
    if (error instanceof TypeError && error.message.includes("network")) {
      throw new NetworkError("Network error: Unable to connect to the API");
    }

    // Otherwise, wrap in a generic API error
    throw new ApiError(
      `API submission failed: ${error instanceof Error ? error.message : String(error)}`,
      undefined,
      undefined,
      payload,
    );
  }
}

export async function submitNewPatientStudyApplicationToApi(patientInput: NewPatientInput) {
  const ketchConsent = await getKetchConsent();
  const payload = prepareNewPatientSubmissionPayload(patientInput, ketchConsent);
  const apiUrl = getApiUrl("refer-patient");
  const responseData = await makeApiCall(apiUrl, payload);
  return { payload, responseData };
}

export async function submitExistingPatientStudyApplicationToApi(patientInput: ExistingPatientInput) {
  const ketchConsent = await getKetchConsent();
  const payload = prepareExistingPatientSubmissionPayload(patientInput, ketchConsent);
  const apiUrl = getApiUrl("refer-existing-patient");
  const responseData = await makeApiCall(apiUrl, payload);
  return { payload, responseData };
}
